/**
 * Columna izquierda de la bandeja: buscador, filtros, lista con scroll infinito.
 */
import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import type { ConversationSummary, WhatsappAccountView } from '@shared/api-types.ts';
import { CONVERSATION_STATUS_LABELS, CONVERSATION_STATUSES } from '@domain/types.ts';
import { formatNumber } from '../format.ts';
import type { AssigneeFilter, StatusFilter } from '../state.ts';
import { EmptyState, ErrorBanner, SegmentedControl, SkeletonRows } from './Feedback.tsx';
import { ConversationListItem } from './ConversationListItem.tsx';
import { Icon } from './Icon.tsx';

const ASSIGNEE_OPTIONS: { value: AssigneeFilter; label: string; title: string }[] = [
  { value: 'all', label: 'Todas', title: 'Sin filtrar por asignacion' },
  { value: 'me', label: 'Mias', title: 'Asignadas a mi' },
  { value: 'unassigned', label: 'Sin asignar', title: 'Sin agente asignado' },
];

export function ConversationList({
  conversations,
  loading,
  loadingMore,
  hasMore,
  error,
  selectedId,
  search,
  status,
  assignee,
  accountId,
  accounts,
  unreadTotal,
  total,
  currentUserId,
  onSearchChange,
  onStatusChange,
  onAssigneeChange,
  onAccountChange,
  onSelect,
  onLoadMore,
  onRetry,
}: {
  conversations: ConversationSummary[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  selectedId: string | null;
  search: string;
  status: StatusFilter;
  assignee: AssigneeFilter;
  accountId: string;
  accounts: WhatsappAccountView[];
  unreadTotal: number;
  total: number | undefined;
  currentUserId: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
  onAssigneeChange: (value: AssigneeFilter) => void;
  onAccountChange: (value: string) => void;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
  onRetry: () => void;
}): ReactElement {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Scroll infinito: cuando el final de la lista entra en pantalla se pide la
  // siguiente pagina. No se recarga nada que ya este en memoria.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !hasMore || loading || loadingMore) return;

    const onScroll = (): void => {
      const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distance < 240) onLoadMore();
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [hasMore, loading, loadingMore, onLoadMore]);

  return (
    <section className="list" aria-label="Conversaciones">
      <div className="list__filters">
        <div className="search">
          <Icon name="search" className="search__icon" />
          <input
            className="input input--search"
            type="search"
            placeholder="Buscar por nombre, telefono o texto"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            aria-label="Buscar conversaciones"
          />
        </div>

        <div className="list__filters-row">
          <select
            className="select"
            value={status}
            onChange={(event) => onStatusChange(event.target.value as StatusFilter)}
            aria-label="Filtrar por estado"
          >
            <option value="all">Todos los estados</option>
            {CONVERSATION_STATUSES.map((value) => (
              <option key={value} value={value}>
                {CONVERSATION_STATUS_LABELS[value]}
              </option>
            ))}
          </select>

          {accounts.length > 1 ? (
            <select
              className="select"
              value={accountId}
              onChange={(event) => onAccountChange(event.target.value)}
              aria-label="Filtrar por cuenta de WhatsApp"
            >
              <option value="all">Todas las cuentas</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        <SegmentedControl
          ariaLabel="Filtrar por asignacion"
          options={ASSIGNEE_OPTIONS}
          value={assignee}
          onChange={onAssigneeChange}
        />

        <div className="list__summary">
          <span>
            {total !== undefined ? `${formatNumber(total)} conversaciones` : `${conversations.length} en pantalla`}
          </span>
          {unreadTotal > 0 ? <span className="list__unread">{formatNumber(unreadTotal)} sin leer</span> : null}
        </div>
      </div>

      <div className="list__scroll" ref={scrollRef}>
        {loading ? <SkeletonRows rows={7} /> : null}

        {!loading && error ? <ErrorBanner message={error} onRetry={onRetry} /> : null}

        {!loading && !error && conversations.length === 0 ? (
          <EmptyState
            icon="inbox"
            title="No hay conversaciones"
            description="Con los filtros actuales no hay ninguna conversacion. Prueba a cambiar el estado o la asignacion, o espera a que entre un mensaje nuevo."
          />
        ) : null}

        {!loading && !error
          ? conversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                selected={conversation.id === selectedId}
                currentUserId={currentUserId}
                onSelect={onSelect}
              />
            ))
          : null}

        {!loading && !error && hasMore ? (
          <div className="list__more">
            <button
              type="button"
              className="button button--ghost button--block"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Cargando…' : 'Cargar mas conversaciones'}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
